<template>
  <v-container fluid class="restock-page">
    <div class="restock-shell">
      <div class="restock-header">
        <v-btn text class="text-none back-btn" @click="goBack">
          <v-icon left small>fal fa-arrow-left</v-icon>
          {{ $t("back") }}
        </v-btn>
        <div class="restock-title">{{ $t("restock") }}</div>
      </div>

      <!-- Step 1: pick items -->
      <template v-if="step === 1">
        <template v-if="showItemsSkeleton">
          <v-card outlined class="rounded-xl restock-card mb-4">
            <v-card-text class="pa-5 pa-md-6">
              <div class="restock-toolbar">
                <div class="skeleton-block skeleton-search"></div>
                <div class="skeleton-block skeleton-supplier"></div>
              </div>
            </v-card-text>
          </v-card>

          <v-card outlined class="rounded-xl restock-card">
            <v-card-text class="pa-5 pa-md-6">
              <v-skeleton-loader type="table-thead" />
              <v-skeleton-loader
                v-for="index in 6"
                :key="`restock-item-skeleton-${index}`"
                type="table-row-divider"
              />
            </v-card-text>
          </v-card>
        </template>

        <template v-else>
        <v-card outlined class="rounded-xl restock-card mb-4">
          <v-card-text class="pa-5 pa-md-6">
          <div class="restock-toolbar">
            <div class="restock-search">
              <TextSearch
                v-model="filters.q"
                :placeholder="$t('search_inventory')"
                @search="fetchItems(1)"
                @onClear="
                  filters.q = '';
                  fetchItems(1);
                "
              />
            </div>
            <div class="restock-supplier">
              <TextSelect
                dense
                searchable
                :placeholder="false"
                label="supplier"
                itemValue="id"
                text="name"
                :returnObject="false"
                :items="supplierFilterOptions"
                v-model="filters.vendor_id"
                @input="fetchItems(1)"
              />
            </div>
          </div>
          </v-card-text>
        </v-card>

        <v-card outlined class="rounded-xl restock-card">
          <v-data-table
            :headers="itemHeaders"
            :items="items"
            :server-items-length="pagination.total"
            :options.sync="options"
            :loading="loadingItems"
            :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            class="restock-table"
          >
            <template v-slot:[`item.no`]="{ index }">
              {{ tableOffset + index + 1 }}
            </template>

            <template v-slot:[`item.item`]="{ item }">
              {{ ingredientName(item) }}
            </template>

            <template v-slot:[`item.in_stock`]="{ item }">
              {{ stockUnitDisplay(item, item.current_stock) }}
            </template>

            <template v-slot:[`item.stock_after`]="{ item }">
              <span v-if="stockAfter(item) === null" class="stock-after-empty">
                &ndash;
              </span>
              <span v-else class="stock-after-value">
                {{ stockUnitDisplay(item, stockAfter(item)) }}
              </span>
            </template>

            <template v-slot:[`item.amount`]="{ item }">
              <div class="qty-stepper">
                <v-btn
                  icon
                  tile
                  class="qty-btn"
                  :disabled="rowQty(item) <= 0"
                  @click="stepQty(item, -1)"
                >
                  <v-icon x-small>fal fa-minus</v-icon>
                </v-btn>
                <v-text-field
                  :value="rowQty(item)"
                  type="number"
                  min="0"
                  dense
                  solo
                  flat
                  hide-details
                  class="qty-input"
                  @input="setQty(item, $event)"
                />
                <v-btn icon tile class="qty-btn" @click="stepQty(item, 1)">
                  <v-icon x-small>fal fa-plus</v-icon>
                </v-btn>
              </div>
            </template>

            <template v-slot:[`item.unit`]="{ item }">
              <div class="unit-cell">
                <v-select
                  :value="rowUnitId(item)"
                  :items="item.restock_units"
                  item-value="unit_id"
                  :item-text="unitOptionText"
                  dense
                  outlined
                  hide-details
                  class="unit-select"
                  @change="setUnit(item, $event)"
                />
                <div v-if="unitConversionHint(item)" class="unit-hint">
                  {{ unitConversionHint(item) }}
                </div>
              </div>
            </template>

            <template v-slot:[`item.price`]="{ item }">
              <div class="price-box">
                {{ pricePerUnit(item) | NumberFormat }} {{ currency }}
              </div>
            </template>

            <template v-slot:[`item.action`]="{ item }">
              <v-btn
                icon
                small
                color="grey"
                :disabled="rowQty(item) <= 0"
                @click="clearRow(item)"
              >
                <v-icon small>fal fa-eraser</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>

        <div class="restock-actions">
          <v-btn
            depressed
            color="#DDEAFD"
            class="text-none primary--text rounded-pill form-action-btn"
            @click="goBack"
          >
            {{ $t("cancel") }}
          </v-btn>
          <v-spacer />
          <v-btn
            depressed
            color="primary"
            class="text-none rounded-pill form-action-btn form-action-btn-primary"
            :disabled="!selectedRows.length"
            @click="goReview"
          >
            {{ $t("continue") }}
          </v-btn>
        </div>
        </template>
      </template>

      <!-- Step 2: review -->
      <template v-else>
        <v-card outlined class="rounded-xl restock-card">
          <v-simple-table class="restock-review-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th scope="col" class="text-left">{{ $t("no") }}</th>
                  <th scope="col" class="text-left">{{ $t("item") }}</th>
                  <th scope="col" class="text-left">{{ $t("quantity") }}</th>
                  <th scope="col" class="text-left">{{ $t("price_per_unit") }}</th>
                  <th scope="col" class="text-right">{{ $t("total") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in selectedRows" :key="row.item.id">
                  <td>{{ index + 1 }}</td>
                  <td>{{ ingredientName(row.item) }}</td>
                  <td>
                    {{ row.received_quantity | NumberFormat }}
                    {{ selectedUnitLabel(row) }}
                  </td>
                  <td>
                    {{ pricePerUnit(row.item, row) | NumberFormat }}
                    {{ currency }}
                  </td>
                  <td class="text-right">
                    {{ lineTotal(row) | NumberFormat }} {{ currency }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>

        <v-card outlined class="rounded-xl restock-card mt-4">
          <v-card-text class="pa-5 pa-md-6">
          <div class="totals-row">
            <span>{{ $t("subtotal") }}</span>
            <span>{{ subtotal | NumberFormat }} {{ currency }}</span>
          </div>
          <div class="totals-row">
            <span>{{ $t("vat") }} {{ vatLabel }}</span>
            <span>{{ vatAmount | NumberFormat }} {{ currency }}</span>
          </div>
          <v-divider class="my-2" />
          <div class="totals-row totals-grand">
            <span>{{ $t("total_amount") }}</span>
            <span class="primary--text"
              >{{ totalAmount | NumberFormat }} {{ currency }}</span
            >
          </div>
          </v-card-text>
        </v-card>

        <div class="restock-actions">
          <v-btn
            depressed
            color="#DDEAFD"
            class="text-none primary--text rounded-pill form-action-btn"
            @click="step = 1"
          >
            {{ $t("back") }}
          </v-btn>
          <v-spacer />
          <v-btn
            depressed
            color="primary"
            class="text-none rounded-pill form-action-btn form-action-btn-primary"
            @click="confirmRestock"
          >
            <v-icon left small>fal fa-check</v-icon>
            {{ $t("confirm_restock") }}
          </v-btn>
        </div>
      </template>
    </div>

    <app-loading :overlay="overlay" />
  </v-container>
</template>

<script>
import { myMixin } from "@/mixin/main";
import TextSearch from "@/components/global/TextSearch";
import TextSelect from "@/components/global/TextSelect";
import { success } from "@/controllers/alert";
import { stockUnitText } from "@/utils/ingredientConversion";
import { mapGetters } from "vuex";

export default {
  name: "IngredientRestockPage",
  mixins: [myMixin],
  components: {
    TextSearch,
    TextSelect,
  },
  data: () => ({
    step: 1,
    overlay: false,
    loadingItems: true,
    hasLoadedItems: false,
    suppliers: [],
    items: [],
    // Selection persists across pages: keyed by ingredient id.
    cart: {},
    filters: {
      q: "",
      vendor_id: "",
    },
    options: {
      page: 1,
      itemsPerPage: 10,
    },
    pagination: {
      total: 0,
      page: 1,
      per_page: 10,
    },
  }),
  computed: {
    ...mapGetters({
      taxInclude: "auth/tax_include",
      taxRate: "auth/tax_rate",
    }),
    itemHeaders() {
      return [
        { text: this.$t("no"), value: "no", sortable: false, width: 60 },
        { text: this.$t("item"), value: "item", sortable: false },
        { text: this.$t("in_stock"), value: "in_stock", sortable: false },
        {
          text: this.$t("stock_after_restock"),
          value: "stock_after",
          sortable: false,
        },
        { text: this.$t("amount_received"), value: "amount", sortable: false },
        { text: this.$t("unit"), value: "unit", sortable: false },
        { text: this.$t("price_per_unit"), value: "price", sortable: false },
        {
          text: this.$t("action"),
          value: "action",
          sortable: false,
          align: "center",
          width: 80,
        },
      ];
    },
    supplierFilterOptions() {
      return [{ id: "", name: this.$t("all_supplier") }, ...this.suppliers];
    },
    showItemsSkeleton() {
      return this.loadingItems && !this.hasLoadedItems;
    },
    tableOffset() {
      return (this.pagination.page - 1) * this.pagination.per_page;
    },
    selectedRows() {
      return Object.values(this.cart).filter(
        (row) => Number(row.received_quantity) > 0
      );
    },
    subtotal() {
      return this.selectedRows.reduce(
        (sum, row) => sum + this.lineTotal(row),
        0
      );
    },
    vatEnabled() {
      return Number(this.taxInclude) === 1;
    },
    vatRate() {
      return Number(this.taxRate || 0);
    },
    vatLabel() {
      return `(${this.vatRate}%)`;
    },
    vatAmount() {
      if (!this.vatEnabled || this.vatRate <= 0) return 0;
      return (this.subtotal * this.vatRate) / 100;
    },
    totalAmount() {
      return this.subtotal + this.vatAmount;
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.fetchItems();
      },
    },
  },
  async created() {
    await this.fetchSuppliers();
    await this.fetchItems();
  },
  methods: {
    async fetchSuppliers() {
      const { data } = await this.$http.get("suppliers");
      if (data.status) {
        this.suppliers = Array.isArray(data.data) ? data.data : [];
      }
    },
    async fetchItems(page) {
      if (page) this.options.page = page;
      try {
        this.loadingItems = true;
        const params = new URLSearchParams();
        if (this.filters.q) params.append("q", this.filters.q);
        if (this.filters.vendor_id) {
          params.append("vendor_id", this.filters.vendor_id);
        }
        params.append("page", this.options.page);
        params.append("per_page", this.options.itemsPerPage);

        const { data } = await this.$http.get(
          `ingredient_restock_items?${params.toString()}`
        );
        if (data.status) {
          this.items = Array.isArray(data.data.data) ? data.data.data : [];
          this.pagination = {
            total: data.data.total || 0,
            page: data.data.current_page || 1,
            per_page: data.data.per_page || this.options.itemsPerPage,
          };
          this.hasLoadedItems = true;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingItems = false;
      }
    },
    ensureCart(item) {
      if (!this.cart[item.id]) {
        this.$set(this.cart, item.id, {
          item,
          received_unit_id: item.unit_id,
          received_quantity: 0,
        });
      }
      return this.cart[item.id];
    },
    rowQty(item) {
      const entry = this.cart[item.id];
      return entry ? Number(entry.received_quantity) : 0;
    },
    rowUnitId(item) {
      const entry = this.cart[item.id];
      return entry ? entry.received_unit_id : item.unit_id;
    },
    setQty(item, value) {
      const entry = this.ensureCart(item);
      const qty = Number(value);
      entry.received_quantity = Number.isFinite(qty) && qty > 0 ? qty : 0;
    },
    stepQty(item, delta) {
      const entry = this.ensureCart(item);
      const next = Number(entry.received_quantity || 0) + delta;
      entry.received_quantity = Math.max(next, 0);
    },
    setUnit(item, unitId) {
      const entry = this.ensureCart(item);
      entry.received_unit_id = unitId;
    },
    clearRow(item) {
      if (this.cart[item.id]) {
        this.cart[item.id].received_quantity = 0;
      }
    },
    unitFactor(item, unitId) {
      const units = item.restock_units || [];
      const match = units.find((u) => u.unit_id === unitId) || units[0];
      if (!match || !match.from_quantity) return 1;
      return match.to_quantity / match.from_quantity;
    },
    // Price per received unit = base unit_cost × (base units per received unit).
    pricePerUnit(item, entry) {
      const unitId = entry ? entry.received_unit_id : this.rowUnitId(item);
      return Number(item.unit_cost || 0) * this.unitFactor(item, unitId);
    },
    // Reads a base-unit amount in the largest restock unit, e.g.
    // "10 Pack / 1 gram". Shared with the ingredients list page.
    stockUnitDisplay(item, baseQuantity) {
      const units = (item.restock_units || [])
        .filter((unit) => unit.from_quantity)
        .map((unit) => ({
          label: this.unitOptionText(unit),
          factor: unit.to_quantity / unit.from_quantity,
        }));

      return stockUnitText(
        baseQuantity,
        units,
        this.unitLabel(item.unit_name, item.unit_name_en),
        this.$options.filters.NumberFormat
      );
    },
    // "1 Pack = 100 gram" for the selected restock unit; empty for the base
    // unit, where the conversion carries no information.
    unitConversionHint(item) {
      const unitId = this.rowUnitId(item);
      const selected = (item.restock_units || []).find(
        (unit) => unit.unit_id === unitId
      );
      if (!selected || !selected.from_quantity) return "";

      const factor = selected.to_quantity / selected.from_quantity;
      if (factor === 1) return "";

      const amount = this.$options.filters.NumberFormat(factor);
      const baseUnit = this.unitLabel(item.unit_name, item.unit_name_en);
      return `1 ${this.unitOptionText(selected)} = ${amount} ${baseUnit}`;
    },
    // Preview of the base-unit stock once this row is received; null until a
    // quantity is entered so untouched rows stay blank.
    stockAfter(item) {
      const qty = this.rowQty(item);
      if (qty <= 0) return null;
      const baseQuantity = qty * this.unitFactor(item, this.rowUnitId(item));
      return Number(item.current_stock || 0) + baseQuantity;
    },
    lineTotal(row) {
      return Number(row.received_quantity) * this.pricePerUnit(row.item, row);
    },
    unitOptionText(unit) {
      return this.unitLabel(unit.name, unit.name_en);
    },
    selectedUnitLabel(row) {
      const units = row.item.restock_units || [];
      const match = units.find((u) => u.unit_id === row.received_unit_id);
      return match ? this.unitOptionText(match) : "";
    },
    unitLabel(name, nameEn) {
      const locale = this.$i18n.locale;
      const label = locale === "en" && nameEn ? nameEn : name;
      return label || "-";
    },
    ingredientName(item) {
      const locale = this.$i18n.locale;
      if (locale === "en" && item.name_en) return item.name_en;
      return item.name_la || item.name_en || "-";
    },
    goReview() {
      if (!this.selectedRows.length) {
        this.$toast.warning(this.$t("no_items_selected"));
        return;
      }
      this.step = 2;
    },
    async confirmRestock() {
      const details = this.selectedRows.map((row) => ({
        ingredient_id: row.item.id,
        received_unit_id: row.received_unit_id,
        received_quantity: Number(row.received_quantity),
      }));

      try {
        this.overlay = true;
        const { data } = await this.$http.post("ingredient_restocks", {
          vendor_id: this.filters.vendor_id || null,
          details,
        });
        if (data.status) {
          await success(this, data.message);
          this.$router.push("/manage-ingredients");
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.overlay = false;
      }
    },
    goBack() {
      this.$router.push("/manage-ingredients");
    },
  },
};
</script>

<style scoped>
.restock-page {
  background: #f4f7fb;
  min-height: calc(100vh - 70px);
}

.restock-shell {
  margin: 0 auto;
  max-width: 1100px;
}

.restock-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.back-btn {
  margin-left: -12px;
  color: #475569;
}

.restock-title {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.restock-card {
  border-color: #e8eef6;
}

/* Table-only cards (no v-card-text wrapper) rely on the v-data-table's /
   v-simple-table's own cell padding, so they get no extra padding here —
   only the toolbar/totals cards use v-card-text pa-5 for that. */

.restock-toolbar {
  align-items: center;
  display: flex;
  gap: 16px;
}

.restock-search {
  flex: 1;
  min-width: 0;
}

.restock-supplier {
  width: 220px;
}

/* Gray-filled, rounded inputs — matching the modern ingredient-form /
   promotion-form field style instead of the old bordered look. */
.restock-search ::v-deep .v-text-field--outlined .v-input__slot,
.restock-supplier ::v-deep .v-input__slot {
  background: #f1f5f9 !important;
  border-radius: 10px !important;
  min-height: 46px;
}

.restock-search ::v-deep .v-text-field--outlined fieldset,
.restock-supplier ::v-deep fieldset {
  border-color: transparent !important;
  transition: border-color 0.2s ease;
}

.restock-search ::v-deep .v-text-field--outlined.v-input--is-focused fieldset,
.restock-supplier ::v-deep .v-input--is-focused fieldset {
  border-color: #438ef2 !important;
  border-width: 1.5px !important;
}

.restock-search ::v-deep .v-input__append-inner,
.restock-supplier ::v-deep .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
}

.restock-search ::v-deep .v-btn.primary {
  border-radius: 999px !important;
  height: 46px !important;
}

@media (max-width: 600px) {
  .restock-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .restock-supplier {
    width: 100%;
  }
}

/* ---- skeleton ---- */
.skeleton-block {
  border-radius: 8px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 37%, #eef1f5 63%);
  background-size: 400% 100%;
  animation: restock-skeleton-shimmer 1.4s ease infinite;
}

@keyframes restock-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.skeleton-search {
  flex: 1;
  min-width: 0;
  height: 40px;
}

.skeleton-supplier {
  width: 220px;
  height: 40px;
}

@media (max-width: 600px) {
  .skeleton-supplier {
    width: 100%;
  }
}

.restock-table::v-deep th {
  color: #6b7280 !important;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.stock-after-empty {
  color: #9ca3af;
}

.stock-after-value {
  color: #059669;
  display: inline-block;
  font-weight: 600;
  white-space: nowrap;
}

.qty-stepper {
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  display: inline-flex;
  height: 38px;
  overflow: hidden;
  padding: 0 2px;
}

.qty-btn.v-btn {
  border-radius: 8px;
  color: #64748b !important;
  height: 30px !important;
  width: 30px !important;
}

.qty-btn.v-btn:not(.v-btn--disabled):hover {
  background: #eef2ff;
  color: #2563eb !important;
}

.qty-input {
  max-width: 44px;
}

.qty-input::v-deep .v-input__control,
.qty-input::v-deep .v-input__slot {
  min-height: 34px !important;
}

.qty-input::v-deep .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.qty-input::v-deep input {
  color: #1f2937;
  font-weight: 600;
  text-align: center;
}

/* Hide the native number-input spin arrows; the +/- buttons drive the value. */
.qty-input::v-deep input::-webkit-outer-spin-button,
.qty-input::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-input::v-deep input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.unit-cell {
  display: inline-block;
}

.unit-select {
  max-width: 130px;
}

.unit-select ::v-deep .v-input__slot {
  background: #f1f5f9 !important;
  border-radius: 10px !important;
  box-shadow: none !important;
  min-height: 38px !important;
}

.unit-select ::v-deep fieldset {
  border-color: transparent !important;
  transition: border-color 0.2s ease;
}

.unit-select ::v-deep .v-input--is-focused fieldset {
  border-color: #438ef2 !important;
  border-width: 1.5px !important;
}

.unit-select ::v-deep .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
}

.unit-select ::v-deep .v-select__selection {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.unit-hint {
  color: #6b7280;
  font-size: 11px;
  line-height: 1.3;
  margin-top: 4px;
  white-space: nowrap;
}

.price-box {
  align-items: center;
  background: #eceff3;
  border-radius: 8px;
  color: #374151;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  height: 38px;
  min-width: 120px;
  padding: 0 12px;
}

.restock-actions {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-top: 20px;
  padding: 0 12px;
}

.form-action-btn {
  height: 44px !important;
  padding: 0 22px !important;
  font-weight: 700;
  border-radius: 999px !important;
  box-shadow: none !important;
  letter-spacing: normal;
}

.form-action-btn >>> .v-btn__content {
  font-size: 14px;
}

.form-action-btn-primary {
  height: 52px !important;
  padding: 0 34px !important;
}

.form-action-btn-primary >>> .v-btn__content {
  font-size: 16px;
}

.restock-review-table::v-deep th {
  color: #6b7280 !important;
  font-size: 12px;
  font-weight: 700;
}

.totals-row {
  color: #1f2937;
  display: flex;
  font-size: 14px;
  justify-content: space-between;
  padding: 6px 0;
}

.totals-grand {
  font-size: 16px;
  font-weight: 700;
}

@media (max-width: 960px) {
  .restock-header {
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .restock-title {
    font-size: 24px;
  }

  .restock-actions {
    flex-wrap: wrap;
    padding: 0;
  }
}
</style>
